// src/pettyCashAdmin/index.js  →  dist/pettyCashAdmin.js  (globalName: PettyCashAdmin)
//
// v129.77 — 💵 Petty Cash Claims, the ADMIN side (approval + oversight).
// Renders into #pettyCashAdminRoot. Matt's ruling 7 Aug 2026:
//   * ONE approval level — AMs approve their own area (the Worker scopes via
//     resolveScopeStores, fail-closed), Head Office approves anything;
//   * every decision records WHO — that's the audit view;
//   * approve mints the APPROVAL CODE shown to the store for till balancing;
//   * reject/return REQUIRE a comment (the store must know why);
//   * monthly overview (month × AED/QAR) + a period report downloadable as
//     CSV, filterable by store / area / country.
//
// Bare-name globals from the admin.html inline core (escapeHtml, showToast,
// DB, storesData…) — typeof-guarded, the classic-script bundle contract.

var _pcView = 'queue';            // queue | all | summary | report
var _pcClaims = [];
var _pcOpenDetail = {};           // claim id → {attachments, events} once loaded

var PC_CATEGORY_LABELS = {
  vehicle: '🚗 Vehicle', taxi: '🚕 Taxi', food: '🍔 Food', travelling: '🚕 Travelling',
  telephone: '📞 Telephone', stationery: '✏️ Stationery',
  marketing: '📣 Marketing', misc: '📦 Misc',
};
var _pcCats = null;   // configured list, learned from the admin list response
function catLabel(key) {
  if (PC_CATEGORY_LABELS[key]) return PC_CATEGORY_LABELS[key];
  var s = String(key || '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

var PC_STATUS_CHIP = {
  submitted: { label: 'Awaiting approval', bg: 'var(--warn-bg)', fg: 'var(--warn-deep)' },
  returned:  { label: 'Returned to store', bg: 'var(--danger-bg)', fg: 'var(--adm-red-text)' },   // legacy pilot rows (v129.80 dropped Return)
  withdrawn: { label: 'Withdrawn by store', bg: 'var(--border)', fg: 'var(--text)' },   // v131.12
  approved:  { label: 'Approved', bg: 'var(--ok-bg)', fg: 'var(--ok-deep)' },
  rejected:  { label: 'Declined', bg: 'var(--danger-bg)', fg: 'var(--danger-deep)' },
  settled:   { label: 'Settled ✓', bg: 'var(--border)', fg: 'var(--text)' },
};

function esc(s) {
  if (typeof escapeHtml === 'function') return escapeHtml(String(s == null ? '' : s));
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function toast(msg, kind) { if (typeof showToast === 'function') showToast(msg, kind); }
function money(n) { return (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtWhen(iso) {
  try {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' +
      String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  } catch (e) { return ''; }
}

// ── Shell ─────────────────────────────────────────────────────────────────────

function render() {
  var root = document.getElementById('pettyCashAdminRoot');
  if (!root) return;
  var tab = function (key, label) {
    var on = _pcView === key;
    return '<button onclick="PettyCashAdmin.setView(\'' + key + '\')" style="border:none;cursor:pointer;font-weight:800;font-size:13px;padding:8px 14px;border-radius:10px;' +
      (on ? 'background:var(--elc-green,#009E3D);color:#fff' : 'background:var(--surface-3);color:var(--text)') + '">' + label + '</button>';
  };
  root.innerHTML =
    // v129.137 — FULL ROLLOUT (Matt, 14 Aug). The v129.78 pilot note is gone,
    // in step with the staff app: both banners went in together and go out together.
    '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px">' +
      tab('queue', '⏳ Approval queue') + tab('all', '📋 All claims') +
      tab('summary', '📅 Monthly overview') + tab('report', '⬇️ Report') +
      tab('cats', '⚙️ Categories') +
    '</div>' +
    '<div id="pcAdminBody"><div style="font-size:13px;color:var(--tiny);padding:14px 2px">Loading…</div></div>';
  if (_pcView === 'summary') return renderSummary();
  if (_pcView === 'report') return renderReport();
  if (_pcView === 'cats') return renderCats();
  return loadClaims();
}

function setView(v) { _pcView = v; render(); }

// ── Queue / all claims ────────────────────────────────────────────────────────

async function loadClaims() {
  var el = document.getElementById('pcAdminBody');
  if (!el) return;
  try {
    var res = await DB.pettyCash.adminList(_pcView === 'queue' ? { status: 'submitted' } : {});
    if (res && Array.isArray(res.categories) && res.categories.length) _pcCats = res.categories;
    _pcClaims = (res && res.claims) || [];
    if (!_pcClaims.length) {
      el.innerHTML = '<div style="font-size:13px;color:var(--tiny);padding:14px 2px">' +
        (_pcView === 'queue' ? 'Nothing waiting for approval. ✓' : 'No claims yet.') + '</div>';
      return;
    }
    el.innerHTML = _pcClaims.map(claimCardHtml).join('');
  } catch (e) {
    el.innerHTML = '<div style="font-size:13px;color:var(--danger);padding:14px 2px">' + esc((e && e.message) || 'Could not load') + '</div>';
  }
}

function claimCardHtml(c) {
  var chip = PC_STATUS_CHIP[c.status] || { label: c.status, bg: 'var(--border)', fg: 'var(--text)' };
  var cat = catLabel(c.category);
  var decidable = c.status === 'submitted' || c.status === 'returned';
  var h = '<div id="pcCard' + c.id + '" style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:10px">' +
    '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;flex-wrap:wrap">' +
      '<div><span style="font-weight:900;font-size:14px">' + esc(c.ticket) + '</span>' +
        ' <span style="font-weight:900;font-size:14px;color:var(--ok-deep)">' + esc(c.currency) + ' ' + money(c.amount) + '</span>' +
        '<div style="font-size:12px;color:var(--tiny);margin-top:2px"><span translate="no">' + esc(c.store) + '</span> · <span>' + esc(cat) + '</span> · ' + esc(c.expense_date) +
        (c.submitted_by_name ? ' · by ' + esc(c.submitted_by_name) : '') + '</div></div>' +
      '<span style="font-size:11px;font-weight:800;padding:3px 9px;border-radius:99px;background:' + chip.bg + ';color:' + chip.fg + '">' + chip.label + '</span>' +
    '</div>' +
    '<div translate="no" style="font-size:13px;margin-top:6px">' + esc(c.narration) + '</div>';   // v135.2: the store's own words
  if (c.approved_by) {
    h += '<div style="font-size:12px;color:var(--ok-deep);margin-top:4px">✓ Approved by <b translate="no">' + esc(c.approved_by) + '</b>' +
      (c.approved_at ? ' · ' + fmtWhen(c.approved_at) : '') +
      (c.approval_code ? ' · code <b style="font-family:monospace">' + esc(c.approval_code) + '</b>' : '') + '</div>';
  }
  if (c.decision_comment && (c.status === 'rejected' || c.status === 'returned')) {
    h += '<div style="font-size:12px;color:var(--danger-deep);margin-top:4px"><b>' + (c.status === 'rejected' ? 'Rejected' : 'Returned') + ':</b> <span translate="no">' + esc(c.decision_comment) + '</span></div>';
  }
  h += '<div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap">' +
    '<button onclick="PettyCashAdmin.toggleDetail(' + c.id + ')" style="border:1px solid var(--border);background:var(--surface-2);color:var(--text);font-weight:700;font-size:12px;padding:7px 12px;border-radius:9px;cursor:pointer">📎 Bills & history</button>';
  if (decidable) {
    // v129.80 (Matt's UAT): TWO decisions — approve, or decline with a typed reason.
    h += '<button onclick="PettyCashAdmin.decide(' + c.id + ',\'approve\')" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:12px;padding:7px 14px;border-radius:9px;cursor:pointer">✓ Approve</button>' +
      '<button onclick="PettyCashAdmin.decide(' + c.id + ',\'reject\')" style="border:none;background:#dc2626;color:#fff;font-weight:800;font-size:12px;padding:7px 12px;border-radius:9px;cursor:pointer">✕ Decline</button>';
  }
  h += '</div><div id="pcDetail' + c.id + '"></div></div>';
  return h;
}

async function toggleDetail(id) {
  var el = document.getElementById('pcDetail' + id);
  if (!el) return;
  if (el.innerHTML) { el.innerHTML = ''; return; }
  el.innerHTML = '<div style="font-size:12px;color:var(--tiny);padding:8px 0">Loading…</div>';
  try {
    var d = _pcOpenDetail[id] || (await DB.pettyCash.adminDetail(id));
    _pcOpenDetail[id] = d;
    var h = '';
    var atts = (d && d.attachments) || [];
    if (!atts.length) h += '<div style="font-size:12px;color:var(--danger-deep);padding:6px 0">⚠️ No bill attached.</div>';
    atts.forEach(function (a, i) {
      if (a.content_type === 'application/pdf') {
        h += '<div style="margin-top:8px"><a href="' + esc(a.data) + '" download="' + esc(a.filename || 'bill.pdf') + '" style="font-size:13px;font-weight:700">📄 ' + esc(a.filename || 'bill.pdf') + ' (download)</a></div>';
      } else {
        // v129.81 (Matt's UAT): tap the bill to ENLARGE full-screen; download
        // link beside it. The viewer reads the cached detail — never a second fetch.
        h += '<div style="margin-top:8px">' +
          '<img src="' + esc(a.data) + '" alt="' + esc(a.filename || 'bill') + '" onclick="PettyCashAdmin.viewAtt(' + id + ',' + i + ')" title="Tap to enlarge" style="max-width:100%;max-height:420px;border-radius:10px;border:1px solid var(--border);cursor:zoom-in">' +
          '<div style="font-size:12px;margin-top:3px">🔍 <a href="#" onclick="PettyCashAdmin.viewAtt(' + id + ',' + i + ');return false" style="font-weight:700">Enlarge</a>' +
          ' · ⬇️ <a href="' + esc(a.data) + '" download="' + esc(a.filename || 'bill.jpg') + '" style="font-weight:700">Download</a></div>' +
        '</div>';
      }
    });
    var evs = (d && d.events) || [];
    if (evs.length) {
      h += '<div style="font-size:11px;font-weight:800;color:var(--tiny);margin-top:10px;text-transform:uppercase;letter-spacing:.5px">History</div>';
      evs.forEach(function (ev) {
        h += '<div style="font-size:12px;color:var(--text);padding:2px 0">' + fmtWhen(ev.created_at) + ' — <b>' + esc(ev.action) + '</b>' +
          (ev.actor ? ' by ' + esc(ev.actor) : '') + (ev.comment ? ' · &quot;' + esc(ev.comment) + '&quot;' : '') + '</div>';
      });
    }
    el.innerHTML = h || '<div style="font-size:12px;color:var(--tiny);padding:6px 0">Nothing to show.</div>';
  } catch (e) {
    el.innerHTML = '<div style="font-size:12px;color:var(--danger);padding:6px 0">' + esc((e && e.message) || 'Could not load') + '</div>';
  }
}

// v129.81 — full-screen bill viewer (Matt's UAT: "download or enlarge the
// attachment"). Reads the already-fetched detail cache; click anywhere closes.
function viewAtt(claimId, idx) {
  var d = _pcOpenDetail[claimId];
  var a = d && d.attachments && d.attachments[idx];
  if (!a || !a.data) { toast('Open Bills & history first', 'error'); return; }
  closeAtt();
  var ov = document.createElement('div');
  ov.id = 'pcAttOverlay';
  ov.style.cssText = 'position:fixed;inset:0;background:rgba(10,14,12,.92);z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:18px;cursor:zoom-out';
  ov.onclick = closeAtt;
  var img = document.createElement('img');
  img.src = a.data;
  img.alt = a.filename || 'bill';
  img.style.cssText = 'max-width:96vw;max-height:86vh;border-radius:10px;box-shadow:0 8px 40px rgba(0,0,0,.5)';
  var bar = document.createElement('div');
  bar.style.cssText = 'margin-top:12px;display:flex;gap:10px;align-items:center';
  bar.onclick = function (e) { e.stopPropagation(); };   // clicking the buttons must not close
  var dl = document.createElement('a');
  dl.href = a.data;
  dl.download = a.filename || 'bill.jpg';
  dl.textContent = '⬇️ Download';
  dl.style.cssText = 'background:var(--card);color:var(--text);font-weight:800;font-size:13px;padding:9px 16px;border-radius:10px;text-decoration:none';
  var cl = document.createElement('button');
  cl.textContent = '✕ Close';
  cl.onclick = closeAtt;
  cl.style.cssText = 'border:1px solid #9aa5a0;background:transparent;color:#fff;font-weight:800;font-size:13px;padding:9px 16px;border-radius:10px;cursor:pointer';
  bar.appendChild(dl);
  bar.appendChild(cl);
  ov.appendChild(img);
  ov.appendChild(bar);
  document.body.appendChild(ov);
}
function closeAtt() {
  var ov = document.getElementById('pcAttOverlay');
  if (ov) ov.remove();
}

async function decide(id, action) {
  var comment = '';
  if (action === 'approve') {
    if (!window.confirm('Approve this claim? An approval code will be issued to the store for till balancing.')) return;
    comment = window.prompt('Optional comment for the store (leave blank for none):') || '';
  } else {
    comment = window.prompt('DECLINE — tell the store why (required):') || '';
    if (!comment.trim()) { toast('A decline needs a typed reason', 'error'); return; }
  }
  try {
    var res = await DB.pettyCash.decide(id, action, comment.trim());
    delete _pcOpenDetail[id];
    if (action === 'approve' && res && res.approval_code) {
      window.alert('Approved ✓\n\nApproval code: ' + res.approval_code + '\n\nThe store sees this code in their app and quotes it when balancing the till.');
    } else {
      toast('Claim ' + (action === 'approve' ? 'approved' : 'declined'));
    }
    loadClaims();
  } catch (e) {
    toast((e && e.message) || 'Decision failed — try again', 'error');
  }
}

// ── Categories editor (v129.80 — Matt's UAT: "change vehicle to taxi") ────────
// One name per line; saved org-wide to app_config. The Worker only lets HEAD
// OFFICE save (a 403 explains that to an AM). Renaming never rewrites history:
// old claims keep the word they were filed under.

async function renderCats() {
  var el = document.getElementById('pcAdminBody');
  if (!el) return;
  if (!_pcCats) {
    try {
      var res = await DB.pettyCash.adminList({ status: 'submitted' });
      if (res && Array.isArray(res.categories) && res.categories.length) _pcCats = res.categories;
    } catch (e) { /* editor still renders with whatever we have */ }
  }
  var cats = _pcCats || Object.keys(PC_CATEGORY_LABELS);
  el.innerHTML =
    '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:16px;max-width:460px">' +
      '<div style="font-weight:900;font-size:14px;margin-bottom:2px">⚙️ Claim categories</div>' +
      '<div style="font-size:12px;color:var(--tiny);margin-bottom:10px">One per line — this is the list stores pick from when submitting. Renaming does not change old claims. Head Office only.</div>' +
      '<textarea id="pcCatsBox" rows="9" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:10px;font-size:14px;box-sizing:border-box;font-family:monospace">' + esc(cats.join('\n')) + '</textarea>' +
      '<button onclick="PettyCashAdmin.saveCats()" style="margin-top:10px;border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:9px 16px;border-radius:10px;cursor:pointer">Save categories</button>' +
      '<div id="pcCatsStatus" style="font-size:12px;margin-top:8px;min-height:16px"></div>' +
    '</div>';
}

async function saveCats() {
  var box = document.getElementById('pcCatsBox');
  var status = document.getElementById('pcCatsStatus');
  var list = String((box && box.value) || '').split('\n').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
  if (!list.length) { if (status) { status.style.color = 'var(--danger)'; status.textContent = 'At least one category needed.'; } return; }
  try {
    var res = await DB.pettyCash.saveCategories(list);
    _pcCats = res.categories || list;
    if (status) { status.style.color = 'var(--adm-ok-text)'; status.textContent = 'Saved ✓ — stores see the new list next time they open Petty cash.'; }
  } catch (e) {
    if (status) { status.style.color = 'var(--danger)'; status.textContent = (e && e.message) || 'Could not save.'; }
  }
}

// ── Monthly overview ──────────────────────────────────────────────────────────

var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

async function renderSummary(year) {
  var el = document.getElementById('pcAdminBody');
  if (!el) return;
  try {
    var res = await DB.pettyCash.summary(year);
    var y = res.year;
    var h = '<div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">' +
      '<button onclick="PettyCashAdmin.summaryYear(' + (y - 1) + ')" style="border:1px solid var(--border);background:var(--card);border-radius:8px;padding:5px 11px;cursor:pointer;font-weight:800">‹</button>' +
      '<div style="font-weight:900;font-size:16px">' + y + '</div>' +
      '<button onclick="PettyCashAdmin.summaryYear(' + (y + 1) + ')" style="border:1px solid var(--border);background:var(--card);border-radius:8px;padding:5px 11px;cursor:pointer;font-weight:800">›</button>' +
      '<div style="font-size:11px;color:var(--tiny)">rejected claims excluded</div></div>';
    h += '<div style="overflow-x:auto"><table style="border-collapse:collapse;width:100%;font-size:13px;background:var(--card);border-radius:12px;overflow:hidden">' +
      '<tr style="background:var(--surface-3);font-weight:800"><td style="padding:8px 10px">Month</td><td style="padding:8px 10px;text-align:end">AED</td><td style="padding:8px 10px;text-align:end">QAR</td><td style="padding:8px 10px;text-align:end">Claims</td></tr>';
    var totA = 0, totQ = 0, totC = 0;
    (res.months || []).forEach(function (m, i) {
      totA += m.AED; totQ += m.QAR; totC += m.count;
      h += '<tr style="border-top:1px solid var(--border)' + (m.count ? '' : ';color:var(--tiny)') + '"><td style="padding:7px 10px;font-weight:700">' + MONTH_NAMES[i] + '</td>' +
        '<td style="padding:7px 10px;text-align:end">' + (m.AED ? money(m.AED) : '—') + '</td>' +
        '<td style="padding:7px 10px;text-align:end">' + (m.QAR ? money(m.QAR) : '—') + '</td>' +
        '<td style="padding:7px 10px;text-align:end">' + (m.count || '—') + '</td></tr>';
    });
    h += '<tr style="border-top:2px solid var(--border);font-weight:900"><td style="padding:8px 10px">Total</td>' +
      '<td style="padding:8px 10px;text-align:end">' + money(totA) + '</td>' +
      '<td style="padding:8px 10px;text-align:end">' + money(totQ) + '</td>' +
      '<td style="padding:8px 10px;text-align:end">' + totC + '</td></tr></table></div>';
    var stores = res.stores || [];
    if (stores.length) {
      h += '<div style="font-weight:900;font-size:14px;margin:16px 2px 8px">By store (' + y + ')</div>' +
        '<div style="overflow-x:auto"><table style="border-collapse:collapse;width:100%;font-size:13px;background:var(--card);border-radius:12px;overflow:hidden">' +
        '<tr style="background:var(--surface-3);font-weight:800"><td style="padding:8px 10px">Store</td><td style="padding:8px 10px;text-align:end">AED</td><td style="padding:8px 10px;text-align:end">QAR</td><td style="padding:8px 10px;text-align:end">Claims</td></tr>';
      stores.forEach(function (s) {
        h += '<tr style="border-top:1px solid var(--border)"><td translate="no" style="padding:7px 10px;font-weight:700">' + esc(s.store) + '</td>' +
          '<td style="padding:7px 10px;text-align:end">' + (s.AED ? money(s.AED) : '—') + '</td>' +
          '<td style="padding:7px 10px;text-align:end">' + (s.QAR ? money(s.QAR) : '—') + '</td>' +
          '<td style="padding:7px 10px;text-align:end">' + s.count + '</td></tr>';
      });
      h += '</table></div>';
    }
    el.innerHTML = h;
  } catch (e) {
    el.innerHTML = '<div style="font-size:13px;color:var(--danger);padding:14px 2px">' + esc((e && e.message) || 'Could not load summary') + '</div>';
  }
}

function summaryYear(y) { renderSummary(y); }

// ── Report (period / store / area / country → table + CSV download) ───────────

function renderReport() {
  var el = document.getElementById('pcAdminBody');
  if (!el) return;
  var now = new Date(Date.now() + 4 * 3600 * 1000);   // Dubai-shifted
  var ym = now.toISOString().slice(0, 7);
  var first = ym + '-01';
  var today = now.toISOString().slice(0, 10);
  // Dropdowns come from the admin inline-core globals: ALL_STORES +
  // getStoreArea/getStoreCountry (the deliberate store-lookup CORE).
  var storeOpts = '<option value="">All stores</option>';
  var areaSet = {}, countrySet = {};
  try {
    (typeof ALL_STORES !== 'undefined' ? ALL_STORES : []).forEach(function (name) {
      storeOpts += '<option value="' + esc(name) + '">' + esc(name) + '</option>';
      if (typeof getStoreArea === 'function') { var a = getStoreArea(name); if (a) areaSet[a] = 1; }
      if (typeof getStoreCountry === 'function') { var c = getStoreCountry(name); if (c) countrySet[c] = 1; }
    });
  } catch (e) {}
  var areaOpts = '<option value="">All areas</option>' + Object.keys(areaSet).sort().map(function (a) { return '<option value="' + esc(a) + '">' + esc(a) + '</option>'; }).join('');
  var countryOpts = '<option value="">All countries</option>' + Object.keys(countrySet).sort().map(function (c) { return '<option value="' + esc(c) + '">' + esc(c) + '</option>'; }).join('');
  el.innerHTML =
    '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:12px">' +
      '<div style="display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end">' +
        '<div><label style="font-size:11px;font-weight:800;display:block;margin-bottom:3px">From</label><input id="pcRepFrom" type="date" value="' + first + '" style="padding:8px;border:1px solid var(--border);border-radius:9px"></div>' +
        '<div><label style="font-size:11px;font-weight:800;display:block;margin-bottom:3px">To</label><input id="pcRepTo" type="date" value="' + today + '" style="padding:8px;border:1px solid var(--border);border-radius:9px"></div>' +
        '<div><label style="font-size:11px;font-weight:800;display:block;margin-bottom:3px">Store</label><select id="pcRepStore" style="padding:8px;border:1px solid var(--border);border-radius:9px;max-width:190px">' + storeOpts + '</select></div>' +
        '<div><label style="font-size:11px;font-weight:800;display:block;margin-bottom:3px">Area</label><select id="pcRepArea" style="padding:8px;border:1px solid var(--border);border-radius:9px">' + areaOpts + '</select></div>' +
        '<div><label style="font-size:11px;font-weight:800;display:block;margin-bottom:3px">Country</label><select id="pcRepCountry" style="padding:8px;border:1px solid var(--border);border-radius:9px">' + countryOpts + '</select></div>' +
        '<button onclick="PettyCashAdmin.runReport()" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:9px 16px;border-radius:10px;cursor:pointer">Run</button>' +
        '<button onclick="PettyCashAdmin.downloadCsv()" style="border:1px solid var(--border);background:var(--surface-2);color:var(--text);font-weight:800;font-size:13px;padding:9px 14px;border-radius:10px;cursor:pointer">⬇️ CSV</button>' +
      '</div>' +
    '</div>' +
    '<div id="pcRepResult"><div style="font-size:12px;color:var(--tiny);padding:6px 2px">Pick a period and Run — or download straight to CSV.</div></div>';
}

function reportParams() {
  return {
    from: (document.getElementById('pcRepFrom') || {}).value || '',
    to: (document.getElementById('pcRepTo') || {}).value || '',
    store: (document.getElementById('pcRepStore') || {}).value || '',
    area: (document.getElementById('pcRepArea') || {}).value || '',
    country: (document.getElementById('pcRepCountry') || {}).value || '',
  };
}

// v129.103 (the ops manager, 10 Aug) — the report row expands to the SAME bills +
// history panel the approval queue uses, so Finance can open the PDF proof of
// a claim without hunting for it in another tab. toggleDetail already renders
// into #pcDetail<id>; we only supply the row to render into.
async function toggleReportRow(id) {
  var tr = document.getElementById('pcRepRow' + id);
  if (!tr) return;
  var open = tr.style.display !== 'none';
  tr.style.display = open ? 'none' : '';
  var box = document.getElementById('pcDetail' + id);
  if (open) { if (box) box.innerHTML = ''; return; }       // collapse clears it
  if (box && !box.innerHTML) await toggleDetail(id);
}

// Finance marks a claim paid/unpaid. Head Office only (the server enforces it
// too). Ticking Paid with no date fills today's Dubai date server-side, and
// the answer repaints Paid by so the audit trail is visible immediately.
async function savePayment(id) {
  var sel = document.getElementById('pcPayStatus' + id);
  var dt = document.getElementById('pcPayDate' + id);
  if (!sel) return;
  var status = sel.value;
  if (status !== 'paid' && dt) dt.value = '';             // clearing Paid clears the date
  try {
    var res = await DB.pettyCash.markPayment(id, status, status === 'paid' ? (dt && dt.value) || null : null);
    if (dt) dt.value = res.payment_date || '';
    sel.style.background = status === 'paid' ? 'var(--ok-bg)' : 'var(--input-bg)';
    var by = document.getElementById('pcPaidBy' + id);
    if (by) by.textContent = res.paid_by || '\u2014';
    toast(status === 'paid' ? 'PC-' + id + ' marked paid' : 'PC-' + id + ' back to unpaid');
  } catch (e) {
    toast((e && e.message) || 'Could not save the payment', 'error');
    // Put the control back where the server actually is, not where the click left it.
    sel.value = status === 'paid' ? 'unpaid' : 'paid';
    sel.style.background = sel.value === 'paid' ? 'var(--ok-bg)' : 'var(--input-bg)';
  }
}

// v129.105 (the ops manager, 10 Aug) — the TOTAL AMOUNT box. Finance works from one
// figure ("the total approved value paid on the 10th"), so the report leads with
// it: everything in view, what has already been paid, and what is still to pay.
// The to-pay figure and the Pay-All button read from the SAME numbers, so the
// button can never claim to pay something the box doesn't show.
//
// Tolerant of an older Worker (a deploy can land the bundle before the Worker):
// if totals.paid/unpaid are absent we show the headline total alone rather than
// rendering "AED 0.00 to pay" and inviting a pointless click.
function totalsBox(t, rowCount, canPay) {
  var split = t && t.unpaid && t.paid;
  var cell = function (label, aed, qar, tone) {
    return '<div style="min-width:150px">' +
      '<div style="font-size:10.5px;font-weight:800;letter-spacing:.03em;text-transform:uppercase;color:var(--sub)">' + esc(label) + '</div>' +
      '<div style="font-size:17px;font-weight:800;color:' + (tone || 'var(--text)') + ';white-space:nowrap">AED ' + money(aed) + '</div>' +
      '<div style="font-size:12px;color:var(--tiny);white-space:nowrap">QAR ' + money(qar) + '</div>' +
    '</div>';
  };
  var h = '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:12px 14px;margin:4px 0 10px;display:flex;gap:22px;flex-wrap:wrap;align-items:center">' +
    cell('Total amount', (t && t.AED) || 0, (t && t.QAR) || 0);
  if (split) {
    h += cell('Already paid', t.paid.AED, t.paid.QAR, 'var(--adm-paid-text)') +
      cell('To pay (' + (t.unpaid_count || 0) + ')', t.unpaid.AED, t.unpaid.QAR, 'var(--adm-amber-text)');
  }
  h += '<div style="flex:1"></div>';
  if (split && canPay && (t.unpaid_count || 0) > 0) {
    h += '<div style="display:flex;gap:8px;align-items:flex-end">' +
      '<div><label style="font-size:11px;font-weight:800;display:block;margin-bottom:3px">Payment date</label>' +
        '<input id="pcPayAllDate" type="date" value="' + esc(todayStr()) + '" style="padding:8px;border:1px solid var(--border);border-radius:9px"></div>' +
      '<button onclick="PettyCashAdmin.payAll()" title="Mark every approved, unpaid claim in this view as paid" ' +
        'style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:9px 16px;border-radius:10px;cursor:pointer">💸 Pay all (' + t.unpaid_count + ')</button>' +
    '</div>';
  }
  h += '</div>' +
    '<div style="font-size:12px;color:var(--tiny);margin:0 2px 8px">' + rowCount + ' claims in view · totals exclude declined</div>';
  return h;
}

// Dubai's today (UTC+4), same shift this file already uses for the report's
// default period — never a naive local/UTC date.
function todayStr() {
  return new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10);
}

function canPayAll() {
  return typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.role === 'admin';
}

// The monthly payment run. Confirms with the COUNT, the MONEY and the DATE
// before anything moves — this is the one bulk write in petty cash, and an
// accidental click would stamp a payment date across a whole month of claims.
async function payAll() {
  if (!canPayAll()) { toast('Head Office only', 'error'); return; }
  var p = reportParams();
  if (!p.from || !p.to) { toast('Pick From and To dates', 'error'); return; }
  var dt = (document.getElementById('pcPayAllDate') || {}).value || '';
  var t = _pcRepTotals || {};
  var msg = 'Mark ' + (t.unpaid_count || 0) + ' approved claim(s) as PAID?\n\n' +
    'AED ' + money(t.unpaid && t.unpaid.AED) + (t.unpaid && t.unpaid.QAR ? '  ·  QAR ' + money(t.unpaid.QAR) : '') + '\n' +
    'Payment date: ' + (dt || 'today') + '\n' +
    (p.store ? 'Store: ' + p.store + '\n' : '') + (p.area ? 'Area: ' + p.area + '\n' : '') + (p.country ? 'Country: ' + p.country + '\n' : '') +
    'Period: ' + p.from + ' to ' + p.to;
  if (typeof confirm === 'function' && !confirm(msg)) return;
  try {
    var res = await DB.pettyCash.payAll(p, dt || null);
    toast(res.paid ? res.paid + ' claim(s) marked paid on ' + res.payment_date : (res.message || 'Nothing to pay'));
    await runReport();                                   // repaint from the server, never from hope
  } catch (e) {
    toast((e && e.message) || 'Pay all failed', 'error');
  }
}

var _pcRepTotals = null;   // the last report's totals — what Pay All quotes back

async function runReport() {
  var el = document.getElementById('pcRepResult');
  if (!el) return;
  var p = reportParams();
  if (!p.from || !p.to) { toast('Pick From and To dates', 'error'); return; }
  el.innerHTML = '<div style="font-size:12px;color:var(--tiny);padding:6px 2px">Running…</div>';
  try {
    var res = await DB.pettyCash.report(p);
    var rows = res.rows || [];
    _pcRepTotals = res.totals || null;
    var h = totalsBox(res.totals, rows.length, canPayAll());
    if (rows.length) {
      h += '<div style="overflow-x:auto"><table style="border-collapse:collapse;width:100%;font-size:12px;background:var(--card);border-radius:12px;overflow:hidden">' +
        '<tr style="background:var(--surface-3);font-weight:800"><td style="padding:7px 9px">Ticket</td><td style="padding:7px 9px">Store</td><td style="padding:7px 9px">Date</td><td style="padding:7px 9px">Category</td><td style="padding:7px 9px;text-align:end">Amount</td><td style="padding:7px 9px">Status</td><td style="padding:7px 9px">Approved by</td><td style="padding:7px 9px">Paid by</td><td style="padding:7px 9px">Code</td><td style="padding:7px 9px">Payment status</td><td style="padding:7px 9px">Payment date</td></tr>';
      rows.forEach(function (r) {
        // v129.103 (the ops manager) — the ticket EXPANDS to the bills + history, so
        // Finance can read the PDF proof without leaving the report.
        var cid = String(r.ticket || '').replace(/^PC-/, '');
        var paid = (r.payment_status || 'unpaid') === 'paid';
        h += '<tr style="border-top:1px solid var(--border)"><td style="padding:6px 9px;font-weight:700">' +
            '<span onclick="PettyCashAdmin.toggleReportRow(' + cid + ')" title="Open the bills and history" ' +
            'style="cursor:pointer;color:var(--adm-paid-text);text-decoration:underline">' + esc(r.ticket) + '</span></td>' +
          '<td translate="no" style="padding:6px 9px">' + esc(r.store) + '</td>' +
          '<td style="padding:6px 9px;white-space:nowrap">' + esc(r.expense_date) + '</td>' +
          '<td style="padding:6px 9px">' + esc(catLabel(r.category)) + '</td>' +
          '<td style="padding:6px 9px;text-align:end;white-space:nowrap">' + esc(r.currency) + ' ' + money(r.amount) + '</td>' +
          '<td style="padding:6px 9px">' + esc(r.status) + '</td>' +
          '<td style="padding:6px 9px">' + esc(r.approved_by || '—') + '</td>' +
          '<td style="padding:6px 9px" id="pcPaidBy' + cid + '">' + esc(r.paid_by || '—') + '</td>' +
          '<td style="padding:6px 9px;font-family:monospace">' + esc(r.approval_code || '—') + '</td>' +
          '<td style="padding:6px 9px">' +
            '<select id="pcPayStatus' + cid + '" onchange="PettyCashAdmin.savePayment(' + cid + ')" ' +
            'style="font-size:11.5px;padding:3px 6px;border-radius:7px;border:1px solid var(--border);font-family:inherit;background:' + (paid ? 'var(--ok-bg)' : 'var(--input-bg)') + '">' +
              '<option value="unpaid"' + (paid ? '' : ' selected') + '>Unpaid</option>' +
              '<option value="paid"' + (paid ? ' selected' : '') + '>Paid</option>' +
            '</select></td>' +
          '<td style="padding:6px 9px"><input type="date" id="pcPayDate' + cid + '" value="' + esc(r.payment_date || '') + '" ' +
            'onchange="PettyCashAdmin.savePayment(' + cid + ')" style="font-size:11.5px;padding:3px 6px;border-radius:7px;border:1px solid var(--border);font-family:inherit"></td></tr>' +
          '<tr id="pcRepRow' + cid + '" style="display:none"><td colspan="11" style="padding:2px 9px 10px;background:var(--surface-2)">' +
            '<div id="pcDetail' + cid + '"></div></td></tr>';
      });
      h += '</table></div>';
    }
    el.innerHTML = h;
  } catch (e) {
    el.innerHTML = '<div style="font-size:13px;color:var(--danger);padding:6px 2px">' + esc((e && e.message) || 'Report failed') + '</div>';
  }
}

async function downloadCsv() {
  var p = reportParams();
  if (!p.from || !p.to) { toast('Pick From and To dates', 'error'); return; }
  try {
    var csv = await DB.pettyCash.reportCsv(p);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'petty-cash_' + p.from + '_' + p.to + '.csv';
    document.body.appendChild(a);
    a.click();
    setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  } catch (e) {
    toast((e && e.message) || 'CSV download failed', 'error');
  }
}

export { render, setView, toggleDetail, toggleReportRow, savePayment, payAll, decide, summaryYear, renderSummary, runReport, downloadCsv, loadClaims, renderCats, saveCats, viewAtt, closeAtt };
